import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X } from '@phosphor-icons/react';
import { useAuth } from '@/lib/auth';
import { usePushSubscription } from '@/lib/push';

const InstallPrompt = () => {
  const { user } = useAuth();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const { status, subscribe } = usePushSubscription(user);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // also show a push prompt after 30s if push not yet subscribed
  const [pushPromptShown, setPushPromptShown] = useState(false);
  useEffect(() => {
    if (!user || pushPromptShown) return;
    const t = setTimeout(() => {
      if (status === 'idle' && Notification?.permission === 'default') setPushPromptShown(true);
    }, 30000);
    return () => clearTimeout(t);
  }, [user, status, pushPromptShown]);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setShow(false); setDeferred(null);
  };

  return (
    <AnimatePresence>
      {show && deferred && (
        <motion.div initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
          className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:w-96 z-[160] glass-hi rounded-2xl p-4 flex items-center gap-3 shadow-2xl"
          data-testid="install-prompt">
          <span className="h-10 w-10 rounded-full bg-gradient-to-br from-bx-cyan via-bx-magenta to-bx-volt breathe shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-sm">Install BubbleX</div>
            <div className="text-xs text-white/60">Get pinged when your bubble travels.</div>
          </div>
          <button onClick={install} data-testid="install-go"
            className="btn-primary px-4 py-2 rounded-full text-xs font-bold">Install</button>
          <button onClick={() => setShow(false)} className="text-white/60">
            <X size={14} weight="bold" />
          </button>
        </motion.div>
      )}
      {pushPromptShown && !show && (
        <motion.div initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
          className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:w-96 z-[160] glass-hi rounded-2xl p-4 flex items-center gap-3 shadow-2xl"
          data-testid="push-prompt">
          <Bell size={22} weight="fill" className="text-bx-cyan shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-sm">Turn on bubble pings?</div>
            <div className="text-xs text-white/60">Hear when your bubble is observed, caught, or returns.</div>
          </div>
          <button onClick={() => { subscribe(); setPushPromptShown(false); }} data-testid="push-go"
            className="btn-primary px-4 py-2 rounded-full text-xs font-bold">Yes</button>
          <button onClick={() => setPushPromptShown(false)} className="text-white/60">
            <X size={14} weight="bold" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InstallPrompt;
